import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { Swiper } from 'swiper/react';
import { Swiper as SwiperType } from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import 'swiper/css/free-mode';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface Props {
	amountPages: number;
	children: React.ReactNode;
	id?: string;
	breakPoints?: any;
	classNameContainer?: string;
	classNameSwiperContainer?: string;
	classNameContainerControls?: string;
}

// Interface para las props del componente CustomPagination
interface CustomPaginationProps {
	swiper: SwiperType | null;
	slidesCount: number;
	currentSlide: number;
}

// Componente de paginación personalizado para móvil
const CustomPagination: React.FC<CustomPaginationProps> = ({
	swiper,
	slidesCount,
	currentSlide,
}) => {
	// Calcular qué dots mostrar (máximo 3)
	const totalDots = slidesCount;
	let dotsToShow: number[] = [];

	if (totalDots <= 3) {
		// Mostrar todos si hay 3 o menos
		dotsToShow = Array.from({ length: totalDots }, (_, i) => i);
	} else {
		// Mostrar máximo 3 dots centrados alrededor del activo
		if (currentSlide === 0) {
			dotsToShow = [0, 1, 2];
		} else if (currentSlide === totalDots - 1) {
			dotsToShow = [totalDots - 3, totalDots - 2, totalDots - 1];
		} else {
			dotsToShow = [currentSlide - 1, currentSlide, currentSlide + 1];
		}
	}

	const goToSlide = (index: number) => {
		if (swiper) {
			swiper.slideTo(index);
		}
	};

	return (
		<div className="flex justify-center items-center space-x-2 md:hidden">
			{dotsToShow.map((index) => (
				<button
					key={index}
					onClick={() => goToSlide(index)}
					className={`w-3 h-3 rounded-full transition-all duration-300 ${
						index === currentSlide ? 'bg-purple-600 scale-125' : 'bg-gray-300'
					}`}
					aria-label={`Ir a slide ${index + 1}`}
				/>
			))}
		</div>
	);
};

const initialBreakPoints = {
	// Móvil
	0: {
		slidesPerView: 1,
		spaceBetween: 20,
	},
	600: {
		slidesPerView: 2,
		spaceBetween: 20,
	},
	// Tablet
	768: {
		slidesPerView: 3,
		spaceBetween: 20,
	},
	// Desktop
	1024: {
		slidesPerView: 2,
		spaceBetween: 40,
	},
	1280: {
		slidesPerView: 2.5,
		spaceBetween: 20,
	},
	// Desktop grande
	1440: {
		slidesPerView: 3.2,
		spaceBetween: 24,
		slidesOffsetBefore: 24,
		slidesOffsetAfter: 30,
	},
	1920: {
		slidesPerView: 3.8,
		spaceBetween: 24,
		slidesOffsetBefore: 24,
		slidesOffsetAfter: 30,
	},
};

export const CarouselComponent: React.FC<Props> = ({
	id = 'default',
	children,
	amountPages,
	breakPoints,
	classNameContainer,
	classNameSwiperContainer,
	classNameContainerControls,
}) => {
	const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
	const [currentSlide, setCurrentSlide] = useState(0);
	const [isMobile, setIsMobile] = useState(false);

	const paginationId = `custom-pagination-${id}`;

	// Detectar si estamos en móvil
	useEffect(() => {
		const checkIsMobile = () => {
			setIsMobile(window.innerWidth < 768);
		};

		// Verificar al montar el componente
		checkIsMobile();

		// Escuchar cambios de tamaño de ventana
		window.addEventListener('resize', checkIsMobile);

		// Limpiar el event listener al desmontar
		return () => {
			window.removeEventListener('resize', checkIsMobile);
		};
	}, []);

	const currentBreakPoints = breakPoints ?? initialBreakPoints;

	return (
		<div
			className={clsx('w-full pb-7', classNameContainer)}
			style={{ contentVisibility: 'auto', containIntrinsicSize: '1px 900px' }}
		>
			<Swiper
				modules={[Pagination, Navigation]}
				grabCursor={true}
				onSwiper={setSwiperInstance}
				onSlideChange={(swiper) => setCurrentSlide(swiper.activeIndex)}
				breakpoints={currentBreakPoints}
				// Configuración condicional de paginación
				pagination={
					!isMobile
						? {
								el: `.${paginationId}`,
								clickable: true,
								renderBullet: (_index, className) => {
									return `<button class="${className} my-custom-dot-class"></button>`;
								},
						  }
						: false
				}
				// navigation={{
				// 	nextEl: '.custom-next-cases',
				// 	prevEl: '.custom-prev-cases',
				// }}
				className={clsx(
					'relative w-full h-[400px] !flex items-center justify-center',
					classNameSwiperContainer
				)}
				wrapperClass="swiper-wrapper my-wrapper !h-[317px]"
			>
				{children}
			</Swiper>

			<div
				className={clsx(
					'flex w-full items-center justify-center lg:justify-start lg:ml-24 gap-x-10 lg:mt-12 px-[45px] lg:px-[85px]',
					classNameContainerControls
				)}
			>
				<button
					className="custom-prev-cases min-w-12 min-h-12 !w-12 !h-12 border border-purple-150 rounded-full flex justify-center items-center"
					onClick={() => swiperInstance?.slidePrev()}
					aria-label="Previous Slide"
				>
					<ChevronLeft
						width={42}
						height={42}
						className="text-primary stroke-1"
					/>
				</button>

				{/* Paginación nativa para escritorio */}
				{!isMobile && (
					<div
						className={`${paginationId} !w-fit items-center md:flex justify-center gap-x-[10px] min-h-[20px]`}
					/>
				)}

				{/* Paginación personalizada para móvil */}
				{isMobile && (
					<CustomPagination
						swiper={swiperInstance}
						slidesCount={amountPages}
						currentSlide={currentSlide}
					/>
				)}

				<button
					className="custom-next-cases min-w-12 min-h-12 !w-12 !h-12 border border-purple-150 rounded-full flex justify-center items-center"
					onClick={() => swiperInstance?.slideNext()}
					aria-label="Next Slide"
				>
					<ChevronRight
						width={42}
						height={42}
						className="text-primary stroke-1"
					/>
				</button>
			</div>
		</div>
	);
};
